import { UPDATABLE_PERSON_FIELD_DEFS } from '@fc/core'
import { Button, Input, Label } from '@fc/ui'
import { GripVertical, Trash2 } from 'lucide-react'
import type { ListSummary } from '../../lib/lists-api'
import type { WorkflowActionInput, WorkflowActionType } from '../../lib/workflows-api'
import { ACTION_LABELS, defaultActionConfig } from './shared'

const selectClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

export interface ActionEditorProps {
  index: number
  action: WorkflowActionInput
  lists: ListSummary[]
  onChange: (next: WorkflowActionInput) => void
  onRemove: () => void
}

export function ActionEditor({ index, action, lists, onChange, onRemove }: ActionEditorProps) {
  const config = action.config ?? {}

  const setType = (type: WorkflowActionType) =>
    onChange({ type, config: defaultActionConfig(type) })
  const setConfig = (patch: Record<string, unknown>) =>
    onChange({ type: action.type, config: { ...config, ...patch } })

  return (
    <div className="rounded-lg border border-border bg-surface-2 p-4">
      <div className="flex items-center gap-2">
        <GripVertical className="size-4 shrink-0 text-dim" aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wider text-dim">
          Step {index + 1}
        </span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onRemove}
          aria-label={`Remove step ${index + 1}`}
        >
          <Trash2 />
        </Button>
      </div>

      <div className="mt-3 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor={`action-type-${index}`}>Action</Label>
          <select
            id={`action-type-${index}`}
            className={selectClass}
            value={action.type}
            onChange={(e) => setType(e.target.value as WorkflowActionType)}
          >
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {(action.type === 'add_to_list' || action.type === 'remove_from_list') && (
          <div className="space-y-1.5">
            <Label htmlFor={`action-list-${index}`}>List</Label>
            <select
              id={`action-list-${index}`}
              className={selectClass}
              value={(config.listId as string) ?? ''}
              onChange={(e) => setConfig({ listId: e.target.value })}
            >
              <option value="">Select a list…</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {action.type === 'update_person_field' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`action-field-${index}`}>Field</Label>
              <select
                id={`action-field-${index}`}
                className={selectClass}
                value={(config.field as string) ?? UPDATABLE_PERSON_FIELD_DEFS[0]!.key}
                onChange={(e) => {
                  const fieldKey = e.target.value
                  const def = UPDATABLE_PERSON_FIELD_DEFS.find((f) => f.key === fieldKey)
                  setConfig({ field: fieldKey, value: def?.options[0]?.value ?? '' })
                }}
              >
                {UPDATABLE_PERSON_FIELD_DEFS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`action-value-${index}`}>Set to</Label>
              <select
                id={`action-value-${index}`}
                className={selectClass}
                value={(config.value as string) ?? ''}
                onChange={(e) => setConfig({ value: e.target.value })}
              >
                {(
                  UPDATABLE_PERSON_FIELD_DEFS.find((f) => f.key === (config.field as string))
                    ?.options ?? UPDATABLE_PERSON_FIELD_DEFS[0]!.options
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {action.type === 'send_notification' && (
          <div className="space-y-1.5">
            <Label htmlFor={`action-message-${index}`}>Message</Label>
            <Input
              id={`action-message-${index}`}
              value={(config.message as string) ?? ''}
              placeholder="e.g. New visitor signed up — follow up this week."
              onChange={(e) => setConfig({ message: e.target.value })}
              maxLength={500}
            />
            <p className="text-xs text-dim">
              Notifications are logged for now (channel delivery is a follow-up).
            </p>
          </div>
        )}

        {action.type === 'webhook' && (
          <div className="space-y-1.5">
            <Label htmlFor={`action-url-${index}`}>Webhook URL</Label>
            <Input
              id={`action-url-${index}`}
              value={(config.url as string) ?? ''}
              placeholder="https://example.com/hooks/fc"
              onChange={(e) => setConfig({ url: e.target.value })}
              maxLength={2000}
            />
            <p className="text-xs text-dim">
              Must be a public https URL. Loopback and private/internal addresses are blocked.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
